import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ShieldAlert } from 'lucide-react'
import type { UserType } from '@/types'
import { workspaceHome } from '@/lib/workspaceHome'
import { getUserTypeDisplayName } from '@/lib/userTypeDisplay'

interface ProtectedRouteProps {
  children: ReactNode
  requireAdmin?: boolean
  /** Restrict to Super Admin ONLY (Admin is not sufficient). Uses the effective
   *  (impersonated) user type so it matches sidebar visibility. */
  requireSuperAdmin?: boolean
  rejectSupport?: boolean
  allowedUserTypes?: UserType[]
}

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication and/or admin privileges.
 *
 * @param children - The component to render if access is granted
 * @param requireAdmin - Whether admin privileges are required (default: false)
 * @param rejectSupport - Whether to block Support users (redirects to dashboard)
 *
 * @example
 * <Route path="/pricebooks" element={
 *   <ProtectedRoute requireAdmin>
 *     <Pricebooks />
 *   </ProtectedRoute>
 * } />
 *
 * @example
 * <Route path="/accounts" element={
 *   <ProtectedRoute rejectSupport>
 *     <Accounts />
 *   </ProtectedRoute>
 * } />
 */
export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  rejectSupport = false,
  allowedUserTypes,
}: ProtectedRouteProps) {
  const { currentUser, userType, isLoading, impersonatedUser } = useUser()

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        role="status"
        aria-live="polite"
        aria-label="Loading user authentication"
      >
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" replace />
  }

  // Redirect Support users to dashboard for non-ticketing pages
  // Also applies when impersonating a Support user
  const effectivelySupport = userType === 'Support' || impersonatedUser?.user_type === 'Support'
  if (rejectSupport && effectivelySupport) {
    return <Navigate to={workspaceHome(userType)} replace />
  }

  // Check allowedUserTypes - Admin/Super Admin always pass
  if (allowedUserTypes) {
    const effectiveType = (impersonatedUser?.user_type || userType) as UserType
    const isAdminUser = ['Admin', 'Super Admin'].includes(effectiveType)
    if (!isAdminUser && !allowedUserTypes.includes(effectiveType)) {
      // Their OWN workspace, not the CRM dashboard. Sending a Training or
      // Employee account to '/' would bounce them into the one area they have no
      // access to at all — see lib/workspaceHome.ts.
      return <Navigate to={workspaceHome(effectiveType)} replace />
    }
  }

  // Access denied if admin/super-admin is required but the user doesn't qualify.
  // requireAdmin: Admin or Super Admin. requireSuperAdmin: Super Admin ONLY (Admin is
  // not sufficient) — checked against the effective (impersonated) type to match the nav.
  const hasAdminPrivilege = ['Admin', 'Super Admin'].includes(userType || '')
  const isEffectiveSuperAdmin = (impersonatedUser?.user_type || userType) === 'Super Admin'
  if ((requireAdmin && !hasAdminPrivilege) || (requireSuperAdmin && !isEffectiveSuperAdmin)) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Alert
          className="max-w-2xl border-destructive/50 bg-destructive/10"
          role="alert"
          aria-live="assertive"
        >
          <ShieldAlert className="h-5 w-5 text-destructive" aria-hidden="true" />
          <AlertTitle className="text-lg font-semibold text-destructive">Access Denied</AlertTitle>
          <AlertDescription className="mt-2 text-sm">
            <p className="mb-2">
              You do not have permission to access this page. This section requires{' '}
              {requireSuperAdmin ? 'Super Administrator' : 'Administrator'} privileges.
            </p>
            <p className="text-muted-foreground">
              If you believe you should have access to this feature, please contact your system
              administrator.
            </p>
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                Current user: <span className="font-medium">{currentUser.name}</span> (
                {currentUser.email})
                <br />
                Role:{' '}
                <span className="font-medium">{getUserTypeDisplayName(userType || 'User')}</span>
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Access granted - render children
  return <>{children}</>
}
